import React from "react";

// Temporary public-facing "platform update in progress" page.
// Premium, responsive, branded (TAVEN gold on black). Replace the "/" route
// back to <Home /> in App.js when the approved release is ready.
export default function PlatformUpdate() {
  return (
    <div
      data-testid="platform-update-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#050505] text-white flex items-center justify-center px-6 py-16"
    >
      <style>{`
        @keyframes pu-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: .35; transform: scale(.7); } }
        @keyframes pu-shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
        @keyframes pu-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes pu-glow { 0%, 100% { opacity: .35; } 50% { opacity: .6; } }
        .pu-anim { opacity: 1; }
      `}</style>

      {/* Ambient gold glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full blur-[120px]"
        style={{ background: "radial-gradient(circle, rgba(197,160,89,0.28), transparent 65%)", animation: "pu-glow 6s ease-in-out infinite" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-160px] right-[-120px] h-[420px] w-[420px] rounded-full blur-[120px]"
        style={{ background: "radial-gradient(circle, rgba(242,101,34,0.14), transparent 60%)" }}
      />
      {/* Fine grid texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(197,160,89,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(197,160,89,0.6) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(circle at 50% 40%, black, transparent 75%)",
          WebkitMaskImage: "radial-gradient(circle at 50% 40%, black, transparent 75%)",
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-2xl text-center">
        {/* Logo */}
        <div className="pu-anim" style={{ animationDelay: "0ms" }}>
          <div className="relative mx-auto mb-8 h-24 w-24 sm:h-28 sm:w-28" style={{ animation: "pu-float 6s ease-in-out infinite" }}>
            <div
              aria-hidden
              className="absolute inset-0 rounded-[26px] blur-2xl"
              style={{ background: "radial-gradient(circle, rgba(197,160,89,0.55), transparent 70%)" }}
            />
            <img
              data-testid="platform-update-logo"
              src="/logo512.png"
              alt="TAVEN"
              className="relative h-full w-full rounded-[26px] ring-1 ring-[#C5A059]/40 shadow-2xl"
            />
          </div>
        </div>

        {/* Status badge */}
        <div className="pu-anim" style={{ animationDelay: "120ms" }}>
          <div
            data-testid="platform-update-badge"
            className="inline-flex items-center gap-2.5 rounded-full border border-[#C5A059]/40 bg-[#C5A059]/10 px-4 py-1.5 backdrop-blur-sm"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#C5A059]" style={{ animation: "pu-pulse 1.8s ease-in-out infinite" }} />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#C5A059]" />
            </span>
            <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-[#E3C892]">
              Platform Update In Progress
            </span>
          </div>
        </div>

        {/* Headline */}
        <h1
          data-testid="platform-update-headline"
          className="pu-anim mt-8 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight"
          style={{ animationDelay: "240ms" }}
        >
          <span className="bg-gradient-to-b from-[#F4E4BE] via-[#C5A059] to-[#9c7c3f] bg-clip-text text-transparent">
            YEDA &amp; TAVEN
          </span>
          <br />
          <span className="text-white">Are Being Upgraded</span>
        </h1>

        {/* Message */}
        <div
          data-testid="platform-update-message"
          className="pu-anim mx-auto mt-6 max-w-xl space-y-4 text-base sm:text-lg leading-relaxed text-white/70"
          style={{ animationDelay: "360ms" }}
        >
          <p>
            We&rsquo;re currently improving the platform to deliver a stronger, faster, and more
            complete experience.
          </p>
          <p>
            A major update is coming very soon &mdash; with new tools, services, and business
            opportunities.
          </p>
        </div>

        {/* Motto */}
        <div className="pu-anim mt-9" style={{ animationDelay: "480ms" }}>
          <p
            data-testid="platform-update-motto"
            className="text-sm sm:text-base font-semibold uppercase tracking-[0.28em] text-[#C5A059]"
          >
            Build. Earn. Grow. Leave a Legacy.
          </p>
        </div>

        {/* Indeterminate progress shimmer */}
        <div className="pu-anim mx-auto mt-9 h-1 w-56 max-w-full overflow-hidden rounded-full bg-white/10" style={{ animationDelay: "560ms" }}>
          <div
            className="h-full w-1/3 rounded-full"
            style={{
              background: "linear-gradient(90deg, transparent, #C5A059, transparent)",
              animation: "pu-shimmer 2s ease-in-out infinite",
            }}
          />
        </div>

        {/* Footer */}
        <div className="pu-anim mt-10 space-y-2" style={{ animationDelay: "680ms" }}>
          <p className="text-sm text-white/60">Thank you for your patience.</p>
          <p data-testid="platform-update-footer" className="text-sm sm:text-base font-medium text-white/80">
            <span className="text-[#C5A059]">YEDA / TAVEN</span> &mdash; The next chapter is coming soon.
          </p>
        </div>
      </div>
    </div>
  );
}
